import { Modal } from "flowbite-react";
import { AnimatePresence, motion } from "motion/react";
import { OnboardingHeader } from "@/features/predictions/components/onboarding/OnboardingHeader";
import { StepDots } from "@/features/predictions/components/onboarding/StepDots";
import { GoodLuckStep } from "@/features/predictions/components/onboarding/steps/GoodLuckStep";
import { NotificationsStep } from "@/features/predictions/components/onboarding/steps/NotificationsStep";
import { RulesStep } from "@/features/predictions/components/onboarding/steps/RulesStep";
import { WelcomeStep } from "@/features/predictions/components/onboarding/steps/WelcomeStep";
import {
  usePronosticsOnboarding,
  type PronosticsOnboardingStep,
} from "@/features/predictions/hooks/usePronosticsOnboarding";

const STEP_VARIANTS = {
  initial: { opacity: 0, x: 16 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
} as const;

const STEP_TRANSITION = { duration: 0.2, ease: "easeOut" } as const;

type StepContentProps = {
  step: PronosticsOnboardingStep
  optOut: boolean
  onOptOutChange: (value: boolean) => void
  isLeaving: boolean
};

const renderStepContent = ({
  step, optOut, onOptOutChange, isLeaving,
}: StepContentProps) => {
  switch (step) {
    case 1:
      return <WelcomeStep />;
    case 2:
      return <RulesStep />;
    case 3:
      return <NotificationsStep />;
    case 4:
      return (
        <GoodLuckStep
          optOut={optOut}
          onOptOutChange={onOptOutChange}
          disabled={isLeaving}
        />
      );
  }
};

export const PronosticsOnboardingWizard = () => {
  const {
    shouldShow,
    currentStep,
    totalSteps,
    next,
    close,
    complete,
    optOut,
    setOptOut,
    isLeaving,
  } = usePronosticsOnboarding();

  const isLastStep = currentStep === totalSteps;
  const handlePrimary = () => {
    if (isLastStep) {
      void complete();
      return;
    }
    next();
  };

  const primaryLabel = (() => {
    if (isLeaving) return "Enregistrement…";
    return isLastStep ? "C'est parti" : "Suivant";
  })();

  return (
    <Modal
      show={shouldShow}
      onClose={close}
      dismissible={false}
      size="3xl"
      className="pronostics-onboarding-modal"
    >
      <div className="flex flex-col overflow-hidden rounded-lg">
        <OnboardingHeader onClose={close} />
        <div className="flex max-h-[60vh] flex-col gap-6 overflow-y-auto bg-white p-6 dark:bg-gray-800 md:p-8">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={currentStep}
              variants={STEP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={STEP_TRANSITION}
            >
              {renderStepContent({
                step: currentStep,
                optOut,
                onOptOutChange: setOptOut,
                isLeaving,
              })}
            </motion.div>
          </AnimatePresence>
          <div className="flex flex-col items-center gap-4 pt-2">
            <StepDots current={currentStep} total={totalSteps} />
            <button
              type="button"
              onClick={handlePrimary}
              disabled={isLeaving}
              className="rounded-full bg-gray-900 px-8 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:focus:ring-white dark:focus:ring-offset-gray-800"
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

PronosticsOnboardingWizard.displayName = "PronosticsOnboardingWizard";
